import { useEffect, useMemo, useState } from 'react'
import { Filter, X } from 'lucide-react'
import type { Product } from '../types'
import {
  buildFacetOptions,
  activeFilterCount,
  EMPTY_PRODUCT_FILTER,
  type ProductFilterState,
  type FacetOption,
} from '../utils/productFilter'

interface Props {
  products: Product[]
  value: ProductFilterState
  onChange: (next: ProductFilterState) => void
}

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]
}

function FacetSection<T extends string>({
  title,
  options,
  selected,
  onToggle,
  searchable = false,
}: {
  title: string
  options: FacetOption<T>[]
  selected: T[]
  onToggle: (val: T) => void
  searchable?: boolean
}) {
  const [q, setQ] = useState('')
  const visible = useMemo(() => {
    if (!searchable || !q.trim()) return options
    const needle = q.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(needle))
  }, [options, q, searchable])

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-1.5">
        <h5 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">{title}</h5>
        {selected.length > 0 && <span className="text-[10px] text-primary">{selected.length} selected</span>}
      </div>
      {searchable && (
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${title.toLowerCase()}…`}
          className="mb-1.5 w-full px-2 py-1 text-[11px] rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:border-primary"
        />
      )}
      <div className="overflow-y-auto max-h-[56vh] pr-1 space-y-0.5">
        {visible.map((o) => (
          <label
            key={o.value}
            className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-[var(--bg-tertiary)] cursor-pointer text-[12px]"
          >
            <input
              type="checkbox"
              checked={selected.includes(o.value)}
              onChange={() => onToggle(o.value)}
              className="accent-primary w-3.5 h-3.5"
            />
            <span className="flex-1 truncate text-[var(--text-primary)]">{o.label}</span>
            <span className="text-[10px] text-[var(--text-muted)]">{o.count}</span>
          </label>
        ))}
        {visible.length === 0 && <p className="text-[11px] text-[var(--text-muted)] px-1.5 py-2">No matches</p>}
      </div>
    </div>
  )
}

/**
 * Filter button + centred modal for the product picker. Multi-select across Country, Product
 * Type, Cascade Type and Data Source (one row of facets) — all derived from the loaded
 * catalogue (see productFilter.ts), so no backend/schema dependency. The button sits between
 * Select All and Clear All.
 */
export function ProductFilterPopover({ products, value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const count = activeFilterCount(value)
  const facets = useMemo(() => buildFacetOptions(products), [products])

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1 px-3 py-1 rounded text-[11px] border transition-colors ${
          count > 0
            ? 'bg-primary/10 border-primary/40 text-primary'
            : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] border-[var(--border-color)] hover:bg-[var(--border-color)]'
        }`}
        title="Filter products by country, type, cascade and data source"
      >
        <Filter size={12} />
        Filter
        {count > 0 && (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full bg-primary text-white text-[9px] font-semibold">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-5xl max-h-[85vh] flex flex-col bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)]">
              <span className="text-sm font-semibold text-[var(--text-primary)]">
                Filter products{count > 0 ? ` · ${count} selected` : ''}
              </span>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[var(--bg-tertiary)]">
                <X size={16} className="text-[var(--text-muted)]" />
              </button>
            </div>

            {/* Facets — one row of four */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <FacetSection
                  title="Country"
                  options={facets.countries}
                  selected={value.countries}
                  onToggle={(v) => onChange({ ...value, countries: toggle(value.countries, v) })}
                  searchable
                />
                <FacetSection
                  title="Product Type"
                  options={facets.types}
                  selected={value.types}
                  onToggle={(v) => onChange({ ...value, types: toggle(value.types, v) })}
                />
                <FacetSection
                  title="Cascade Type"
                  options={facets.cascades}
                  selected={value.cascades}
                  onToggle={(v) => onChange({ ...value, cascades: toggle(value.cascades, v) })}
                />
                <FacetSection
                  title="Data Source"
                  options={facets.dataSources}
                  selected={value.dataSources}
                  onToggle={(v) => onChange({ ...value, dataSources: toggle(value.dataSources, v) })}
                  searchable
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-color)]">
              <button
                onClick={() => onChange(EMPTY_PRODUCT_FILTER)}
                disabled={count === 0}
                className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Clear all filters
              </button>
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-1.5 rounded text-[12px] font-medium bg-primary text-white hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
